import { createSlice } from "@reduxjs/toolkit";
import loadEnableComplianceVariant from "./actions/loadEnableComplianceVariant";
import loadLoyaltyIsBenefitAvailable from "./actions/loadLoyaltyIsBenefitAvailable";
import loadLoyaltyCustomerTierId from "./actions/loadLoyaltyCustomerTierId";
import loadLoyaltyGetActiveMembersDaySaleEvent from "./actions/loadLoyaltyGetActiveMembersDaySaleEvent";
import loadLoyaltyIsBenefitEventActive from "./actions/loadLoyaltyIsBenefitEventActive";
import loadLoyaltyIsEnabled from "./actions/loadLoyaltyIsEnabled";
import loyaltyOptIn from "./actions/loyaltyOptIn";

// Platforms
export const MOBILE = "mobile";
export const TABLET = "tablet";
export const DESKTOP = "desktop";

// Gender
export const FLOOR_WOMEN = "women";
export const FLOOR_MEN = "men";
export const FLOOR_DEFAULT = "default";

// Loyalty
export const MEMBERS_ONLY_SALE = "MembersOnlySale";
export const MEMBERS_SALE_LIVE = "MembersSaleLive";
export const MEMBERS_PRE_SALE = "MembersPreSale";
export const EARLY_ACCESS_SALE = "EarlyAccessToSale";

// TODO: grab this from config.countries
export const initialState = {
  locale: "en-GB",
  country: {
    siteId: 1,
    storeCode: "COM",
    defaultLanguage: "en-GB",
    defaultSizeSchema: "UK",
    defaultCurrency: "GBP",
    url: "http://www.asos.com/",
    countryCode: "GB",
    imageUrl: "/assets/storesa/images/flags/gb.png",
    name: "United Kingdom",
    majorCountry: null,
    isDefaultCountry: true,
    keyStoreDataVersion: 1
  },
  geolocation: {},
  loyalty: {
    isEnabled: false,
    isOptedIn: false,
    tier: null,
    isBenefitAvailable: {},
    isBenefitActive: {},
    membersDaySaleEvent: null,
    enableComplianceVariant: false
  },
  platform: MOBILE
};

const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    setAppVersion: (state, { payload }) => {
      state.appVersion = payload;
    },
    setFloor: (state, { payload }) => {
      state.floor = payload;
    },
    setLocale: (state, { payload }) => {
      state.locale = payload;
    },
    setPlatform: (state, { payload }) => {
      state.platform = payload;
    },
    setSizeSchema: (state, { payload }) => {
      state.sizeSchema = payload;
    },
    setCountry: (state, { payload }) => {
      state.country = payload;
    },
    setCurrency: (state, { payload }) => {
      state.currency = payload;
    },
    setStoreUrl: (state, { payload }) => {
      state.storeUrl = payload;
    },
    setGeolocation: (state, { payload }) => {
      state.geolocation = payload;
    },
    setLoyaltyIsEnabled: (state, { payload }) => {
      state.loyalty.isEnabled = payload;
    },
    setMembersDaySaleEvent: (state, { payload }) => {
      state.loyalty.membersDaySaleEvent = payload;
    },
    setLoyaltyTier: (state, { payload }) => {
      state.loyalty.tier = payload;
    },
    setLoyaltyIsOptedIn: (state, { payload }) => {
      state.loyalty.isOptedIn = payload;
    },
    setIsBenefitAvailable: {
      reducer: (state, { payload }) => {
        state.loyalty.isBenefitAvailable = {
          ...state.loyalty.isBenefitAvailable,
          ...payload
        };
      },
      prepare: (isBenefitAvailable, benefitType) => ({
        payload: { [benefitType]: isBenefitAvailable }
      })
    },
    setIsBenefitEventActive: {
      reducer: (state, { payload }) => {
        state.loyalty.isBenefitActive = {
          ...state.loyalty.isBenefitActive,
          ...payload
        };
      },
      prepare: (isBenefitActive, benefitType) => ({
        payload: { [benefitType]: isBenefitActive }
      })
    },
    setEnableComplianceVariant: (state, { payload }) => {
      state.loyalty.enableComplianceVariant = payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(
      loadLoyaltyCustomerTierId.fulfilled,
      (state, { payload }) => {
        state.loyalty.tier = payload;
      }
    );
    builder.addCase(loadLoyaltyIsEnabled.fulfilled, (state, { payload }) => {
      if (typeof payload === "boolean") {
        state.loyalty.isEnabled = payload;
      }
    });
    builder.addCase(
      loadLoyaltyIsBenefitAvailable.fulfilled,
      (state, { payload }) => {
        if (!payload.isAuthenticated) {
          state.loyalty.tier = null;

          return;
        }

        state.loyalty.isBenefitAvailable = {
          ...state.loyalty.isBenefitAvailable,
          [payload.loyaltyBenefitType]: payload.isBenefitAvailable
        };
      }
    );
    builder.addCase(
      loadLoyaltyIsBenefitEventActive.fulfilled,
      (state, { payload }) => {
        state.loyalty.isBenefitActive = {
          ...state.loyalty.isBenefitActive,
          [payload.loyaltyBenefitType]: payload.isActive
        };
      }
    );
    builder.addCase(
      loadLoyaltyGetActiveMembersDaySaleEvent.fulfilled,
      (state, { payload }) => {
        state.loyalty.membersDaySaleEvent = payload;
      }
    );
    builder.addCase(loyaltyOptIn.fulfilled, (state, { payload }) => {
      state.loyalty.tier = payload;
      state.loyalty.isOptedIn = true;
    });
    builder.addCase(
      loadEnableComplianceVariant.fulfilled,
      (state, { payload }) => {
        if (typeof payload === "boolean") {
          state.loyalty.enableComplianceVariant = payload;
        }
      }
    );
  }
});

export const {
  setAppVersion,
  setFloor,
  setLocale,
  setPlatform,
  setCountry,
  setCurrency,
  setStoreUrl,
  setSizeSchema,
  setGeolocation,
  setLoyaltyIsEnabled,
  setLoyaltyTier,
  setLoyaltyIsOptedIn,
  setMembersDaySaleEvent,
  setIsBenefitAvailable,
  setIsBenefitEventActive,
  setEnableComplianceVariant
} = configSlice.actions;

export default configSlice.reducer;
